import {
    BaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart';
import flatten from 'lodash/flatten';
import {
    convertCategoryToNumber,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/scatter-chart';
import {
    DoubleValueAxisTrendlineProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline';
import {
    AxisName,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import { DataLabelProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label';
import { STDLblPos, STSizeRepresents, SubChartType } from '../../../Interface/chart-v3/sdk-model.interface';
import type {
    IChartSeries,
    ITrendline,
    IDataLabel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { DataListItem } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import type { IBubbleChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type {
    IBubbleChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/bubble-chart.interface';
import type {
    IAxisProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import { IBubbleChartDataItem } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import { IBubbleChartDataItemProperty } from './data-item/data-item.interface';
import { BubbleChartDataItemProperty } from './data-item';

export class BubbleChartProperty extends BaseSubChartProperty<IBubbleChart> implements IBubbleChartProperty {
    public declare data: IBubbleChartDataItemProperty[];
    private maxBubbleSize: number;
    private maxBubbleRadius: number;

    public get bubble3D(): boolean {
        return this.uiData.bubble3D;
    }
    public get bubbleScale(): number {
        return this.uiData.bubbleScale;
    }
    public get sizeRepresents(): STSizeRepresents {
        return this.uiData.sizeRepresents;
    }
    public get showNegBubbles(): boolean {
        return !!this.uiData.showNegBubbles;
    }

    public getAxisValuesFromData(axis: IAxisProperty) {
        let values: number[] = [];
        // 主要纵轴取values，否则取category
        if (axis.name === AxisName.PRIMARY_VERTICAL) {
            values = this.data.map(data => data.value).filter(i => !isNaN(i));
        } else {
            values = this.data.map(data => data.category as number).filter(i => !isNaN(i));
        }

        return { values };
    }

    /**
     * 气泡图相比其他的图形，需要在坐标轴第一次计算的基础上，
     * 加入气泡半径是否超出坐标轴范围的判定，并进行相对应的坐标轴范围扩充
     * 以让气泡完整的展示在图表内
     */
    public processAxis(axis: IAxisProperty) {
        if (axis.name ===  AxisName.PRIMARY_VERTICAL) {
            this.extendPrimaryVerticalAxisScale();
        } else {
            this.extendPrimaryHorizonAxisScale();
        }
    }


    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IBubbleChartDataItemProperty) {
        return new BubbleDataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public updateBubbleSizeRepresent(value: STSizeRepresents) {
        const subChartModel = this.context.getSubChartModel() as IBubbleChart;
        if (subChartModel.originType !== SubChartType.BubbleChart) return;
        subChartModel.updateProps({ sizeRepresents: value });
    }

    public getBubbleSizeRepresent(): STSizeRepresents | undefined {
        const subChartModel = this.context.getSubChartModel() as IBubbleChart;
        if (subChartModel.originType !== SubChartType.BubbleChart) return;
        return subChartModel.sizeRepresents;
    }


    protected initDataItem(param: {
        dataItem: DataListItem,
        index: number,
        serItem: IChartSeries,
        serIndex: number,
    }): IBubbleChartDataItemProperty {
        const dataItem = new BubbleChartDataItemProperty(param.dataItem, this.context);
        const { serItem, index } = param;
        const bubbleSize = serItem.bubbleSize?.[index] || 0;
        dataItem.init({
            ...param,
            size: this.calculateBubbleSize(bubbleSize),
            originSize: bubbleSize,
        });
        return dataItem;
    }

    protected initFlattenData() {
        super.initFlattenData();

        this.data = convertCategoryToNumber(this.data);
    }

    protected getTrendlineProperty(trendline: ITrendline, ser: IChartSeries) {
        return new DoubleValueAxisTrendlineProperty(trendline, this.context).init(this.data, ser);
    }

    private extendPrimaryVerticalAxisScale() {
        if (this.yAxis) {
            // 所有的数据点
            const values = this.data.map(item => item.value || 0);
            // TODO 这里之前是取的g2计算后的plotBoxHeight，有一点出入，大小可能不一致，看怎么解决
            this.extendAxisScale(values, this.yAxis, this.chartFrame.height);
        }
    }
    private extendPrimaryHorizonAxisScale() {
        if (this.xAxis) {
            const values = this.data.map(item => Number(item.category) || 0);
            this.extendAxisScale(values, this.xAxis, this.chartFrame.width);
        }
    }

    private extendAxisScale(values: number[],  axis: IAxisProperty, axisLength: number) {
        // 先通过气泡的中心点，算出大概的坐标相关数据
        const { max = 0, min = 0 } = axis.scale || {};

        const maxValues: number[] = [];
        const minValues: number[] = [];
        // 再通过气泡半径和整个绘图区的比例，推算出需要拓展最大最小值的范围
        values.forEach((value, index) => {
            const chartDataItem = this.data[index];
            if (this.isInvalidDataItem(chartDataItem)) {
                return;
            }

            const { size: radius, originSize } = chartDataItem;
            // 不显示的点不参与计算
            if (!this.showNegBubbles && originSize < 0) {
                return;
            }

            // 通过半径在坐标轴长度上的占比，计算出 需要在球的原数据点，拓展出多少的位置，来防止球超出绘图区
            const extendValue = (max - min) * (radius / axisLength);

            // 计算出每个气泡两端的value，以用于计算坐标轴需要两边扩展的距离
            maxValues.push(value + extendValue);
            minValues.push(value - extendValue);
        });

        const extendValueMax = Math.max(...maxValues);
        const extendValueMin = Math.min(...minValues);

        axis.setValues(axis.values, [extendValueMin, extendValueMax]);
    }


    private get chartFrame() {
        return {
            width: this.context.getChartModel().getFrame()
                .getWidth(),
            height: this.context.getChartModel().getFrame()
                .getHeight(),
        };
    }

    private calculateBubbleSize(size: number) {
        const originSize = Math.abs(size);
        if (!this.maxBubbleSize) {
            const sizeList = flatten(this.series.map((ser) => {
                const { bubbleSize, dataList } = ser;

                // 横/纵坐标缺失的点，不会展示。这里把不展示的点对应的bubbleSize过滤掉，不影响整体气泡的比例
                return bubbleSize?.filter((size, index) => {
                    const point = dataList[index];
                    return !this.isIgnoreBubbleSize(point);
                }) || [];
            }))
                .filter((item) => {
                    if (item === undefined || (!this.showNegBubbles && item < 0)) {
                        return false;
                    }
                    return true;
                })// 取绝对值计算size
                .map(item => Math.abs(item!));


            this.maxBubbleSize = Math.max(...sizeList);

            const { width, height } = this.chartFrame;
            // 以较小边为基准
            const baseLineLength = Math.min(width, height);

            // 逆向出来的结果是：
            //  1. 最大的bubble直径，是较短plotBox的边长的 3/13
            this.maxBubbleRadius = baseLineLength * (3 / 13) / 2;
        }

        const { bubbleScale, sizeRepresents } = this;
        let geometryScale = bubbleScale / 100;
        if (geometryScale > 1) {
            geometryScale = geometryScale * (5 / 6);
        } if (geometryScale < 1) {
            geometryScale = geometryScale / (5 / 6);
        }

        if (sizeRepresents === STSizeRepresents.w) {
            // 如果大小表示为"气泡宽度"
            // 除2是计算半径， 是canvas缩放
            // return originSize / 2 * CANVAS_PT_SCALE * scale;
            const calSize = (originSize / this.maxBubbleSize) * this.maxBubbleRadius * geometryScale;
            return calSize;
        }

        // 如果大小表示为"面积"，以maxRadius为基准（两种BubbleSizeRepresents，最大bubble的半径大小一样），来计算实际的半径大小。
        // const size10Radius = 10 / 2 * CANVAS_PT_SCALE * scale;
        const calSize =  this.maxBubbleRadius * Math.sqrt(originSize / this.maxBubbleSize) * geometryScale;
        return calSize;
    }

    private isIgnoreBubbleSize(point: DataListItem) {
        const isValueNil = this.isValueNil(point?.valuePoint?.value);
        const isCategoryNil = this.isValueNil(point?.categoryPoint?.value);

        return isValueNil || isCategoryNil;
    }

    private isInvalidDataItem(dataItem: IBubbleChartDataItem) {
        return this.isValueNil(dataItem.value) || this.isValueNil(dataItem.category);
    }

    private isValueNil(value) {
        return value === '' || value === undefined || value === null;
    }
}

class BubbleDataLabelProperty extends DataLabelProperty {
    public get labelPosition(): STDLblPos {
        return this.uiData.labelPosition || STDLblPos.r;
    }
}
